import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEstateBalanceSheet_UpdateEstateBalanceSheetMutation } from 'queries';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import Modal from 'common/modal';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  content: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
  },
  title: {
    fontWeight: 600,
    fontSize: '18px',
    marginTop: '18px',
    marginBottom: '32px',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '24px',
  },
}));

type Props = {
  title: string;
  showModal: boolean;
  onClose: () => void;
  estateBalanceSheetId: string;
  portalId: string;
  column: string;
  currentValue?: number | null;
};

const schema = yup.object().shape({
  value: yup.number().required('Required').typeError('Enter a valid number'),
});

type FormValues = {
  value: number;
};

const EditAccountSummaryFieldModal = ({
  title,
  column,
  portalId,
  showModal,
  onClose,
  estateBalanceSheetId,
  currentValue,
}: Props) => {
  const classes = useStyles();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    values: {
      value: currentValue || 0,
    },
  });

  const [updateEstateBalanceSheet, { loading: updateEstateBalanceSheetExecuting }] =
    useEstateBalanceSheet_UpdateEstateBalanceSheetMutation({
      onError: (error) => {
        showAlert({
          message: 'Error updating account summary: ' + error.message,
          severity: NotificationSeverity.ERROR,
        });
      },
      onCompleted: () => {
        showToast({
          message: 'Account summary updated successfully',
          severity: NotificationSeverity.SUCCESS,
        });
        onClose();
      },
    });

  const onSubmit = async (data: any) => {
    updateEstateBalanceSheet({
      variables: {
        input: {
          id: estateBalanceSheetId,
          portalId,
          [column]: data.value,
        },
      },
    });
  };

  return (
    <Modal
      setShowModal={onClose}
      onExitClick={onClose}
      showModal={showModal}
      showActions={false}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.content}>
            <Typography className={classes.title}>{title}</Typography>
            <Controller
              name="value"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  size="small"
                  autoFocus
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.value?.message}
                />
              )}
            />
            <div className={classes.buttonContainer}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || updateEstateBalanceSheetExecuting}
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
};

export default EditAccountSummaryFieldModal;
